/**
 * The app's React error boundary (mounted in main.tsx). The user-facing
 * surface is minimal — a short message + a Submit Support Ticket button —
 * because the previous dialogue exposed fake support contact info and a
 * Developer Info dump to end users. Full diagnostics are still captured and
 * POSTed to the backend `/api/errors/report` endpoint for the gated developer
 * page; the user just doesn't see them.
 */

import React from 'react';
import * as Sentry from '@sentry/react';

// The one message the boundary shows: the SYS_001 entry of the old root
// config/messages catalogue (deleted, CONFIG CF-0). No code emits any of
// that catalogue's other codes, so a render error carrying some other
// `code` shows no message card, exactly as before.
const UNEXPECTED_ERROR_CODE = 'SYS_001';
const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred. We\'re working to fix this.';

// ============================================================================
// Background error reporting
// ============================================================================

/**
 * Fire-and-forget POST to the backend. Swallows every failure path —
 * the whole point is that an error reporter must never itself raise a
 * second error UI when it can't reach the network.
 */
function reportErrorToBackend(payload: Record<string, unknown>): void {
  try {
    // Mirror http-client.ts's URL convention: VITE_API_BASE_URL already
    // carries the '/api' prefix ('/api' in local dev, 'https://host/api'
    // on prod) and endpoints are appended without their own '/api'. The
    // previous hardcoded '/api/errors/report' doubled the prefix whenever
    // the base var was set ('/api' + '/api/errors/report' ->
    // '/api/api/errors/report' -> 404).
    const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '';
    const url = `${apiBaseUrl}/errors/report`;
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    }).catch(() => {
      /* swallow — never surface a second error UI */
    });
  } catch {
    /* swallow */
  }
}

// ============================================================================
// Error Display
// ============================================================================

/**
 * Minimal inline error display. Renders a short user-facing message and
 * a single "Submit support ticket" button. No fake support email/phone,
 * no recovery-suggestion list, no Developer Info panel.
 */
function ErrorDisplay({ errorCode }: { errorCode: string }): JSX.Element | null {
  if (errorCode !== UNEXPECTED_ERROR_CODE) return null;
  return (
    <div className="border rounded-lg p-4 border-red-300 bg-red-50 text-red-900">
      <h3 className="font-semibold mb-2">An error occurred</h3>
      <p className="text-sm">{UNEXPECTED_ERROR_MESSAGE}</p>
      <div className="mt-4">
        <button
          type="button"
          onClick={() => window.location.assign('/submit-to-research')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
        >
          Submit support ticket
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// React Error Boundary — minimal user-facing card; full diagnostics POSTed
// in the background to /api/errors/report for the gated developer surface.
// ============================================================================

interface ErrorBoundaryState {
  hasError: boolean;
  errorCode?: string;
  error?: Error;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> },
  ErrorBoundaryState
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const errorCode = (error as any).code;
    return { hasError: true, errorCode, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
    // Send to Sentry (no-op unless VITE_SENTRY_DSN is configured). React
    // render errors caught here aren't "unhandled", so Sentry wouldn't
    // see them without an explicit capture.
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    reportErrorToBackend({
      error_code: (error as any).code || UNEXPECTED_ERROR_CODE,
      message: error.message,
      stack: error.stack,
      component_stack: errorInfo.componentStack,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const Fallback = this.props.fallback;
        return <Fallback error={this.state.error!} />;
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <ErrorDisplay
              errorCode={this.state.errorCode || UNEXPECTED_ERROR_CODE}
            />
            <button
              onClick={() => window.location.reload()}
              className="mt-4 w-full px-4 py-2 bg-primary/50 text-white rounded hover:bg-primary"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
